import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';

export interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectProps {
  value: string | number;
  onChange: (value: string | number) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  className = '',
  disabled = false,
}: SelectProps) {
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className={`relative ${className}`}>
        <ListboxButton
          className={`
            relative w-full cursor-pointer rounded-lg border border-pb-gray-200 bg-white py-2 pl-3 pr-10 text-left
            focus:border-pb-orange focus:ring-1 focus:ring-pb-orange focus:outline-none
            disabled:cursor-not-allowed disabled:bg-pb-gray-50 disabled:text-pb-gray-400
            transition-colors
          `}
        >
          <span className={`block truncate ${!selectedOption ? 'text-pb-gray-400' : 'text-pb-gray-800'}`}>
            {selectedOption?.label || placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDown className="h-4 w-4 text-pb-gray-400" aria-hidden="true" />
          </span>
        </ListboxButton>

        <ListboxOptions
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option.value}
              className={({ active, selected }) =>
                `relative cursor-pointer select-none py-2 pl-10 pr-4 transition-colors ${
                  active ? 'bg-[#245982] text-white' : 'text-pb-gray-800'
                } ${selected && !active ? 'bg-pb-gray-50' : ''}`
              }
            >
              {({ selected, active }) => (
                <>
                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                    {option.label}
                  </span>
                  {selected && (
                    <span
                      className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                        active ? 'text-white' : 'text-[#245982]'
                      }`}
                    >
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
